'use client'
import { zodResolver } from '@hookform/resolvers/zod'
import FormComponents from '@/components/FormComponents'
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
    firmResponseSchema,
    getRegistrationSchema,
    registrationResponseSchema,
    RegistrationSchema,
} from '@/eshop/utils/schema'
import { eshopInputClasses } from '@/eshop/constants'
import EshopFormHeading from '../FormHeading'
import { EshopFormButton } from '../FormButton'
import { CountriesConnection } from '@/eshop/gql/graphql'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/Button'
import { useTranslation } from 'react-i18next'
import { useSchemaTranslations } from '@/eshop/hooks/useSchemaTranslations'
import { EshopContext } from '../EshopProvider'
import { Select } from '@/components/FormComponents/select'
import { ArrowDownTrayIcon } from '@heroicons/react/20/solid'
import Loading from '@/app/eshop/user/loading'
import { Heading } from '@/components/Heading'
import clsx from 'clsx'
import Script from 'next/script'

const { TextInput } = FormComponents

declare global {
    interface Window {
        grecaptcha: any
    }
}

export interface Props {
    countries: CountriesConnection | null
    defaultCountry: number
    onDone?: (name: string, password: string) => void
}

export default function RegistrationForm({
    countries,
    onDone,
    defaultCountry,
}: Props) {
    const { t } = useTranslation()
    const { country } = useContext(EshopContext)
    const schemaT = useSchemaTranslations()
    const {
        register,
        handleSubmit,
        reset,
        control,
        getValues,
        setValue,
        formState: { errors },
    } = useForm<RegistrationSchema>({
        resolver: zodResolver(getRegistrationSchema(schemaT, country)),
        mode: 'onChange',
    })

    const savedIco = useRef<string>('')
    const isAresUnavailable = useRef<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [isFirmSectionDisabled, setIsFirmSectionDisabled] = useState(false)
    const formData = useRef<RegistrationSchema | null>(null)

    const shouldApplyRecaptcha = useMemo(() => {
        const isProduction = process.env.NODE_ENV === 'production'
        const isDefaultCs =
            process.env.NEXT_PUBLIC_ESHOP_DEFAULT_COUNTRY_ID === '45'
        return isProduction && isDefaultCs
    }, [country])

    useEffect(() => {
        if (!isAresUnavailable.current && country === 'cs') {
            setIsFirmSectionDisabled(true)
        }
    }, [isAresUnavailable, country])

    useEffect(() => {
        const cc = countries?.edges?.find(
            (c) => c.node.countryId === defaultCountry
        )
        if (cc) {
            setValue('country', defaultCountry.toString())
        }
    }, [countries?.edges, defaultCountry, setValue])

    const countryOptions = useMemo(
        () =>
            countries?.edges
                ? countries.edges.map((e) => ({
                      label: e.node.name,
                      value: e.node.countryId.toString(),
                  }))
                : [],
        [countries]
    )

    const { mutate, isPending } = useMutation({
        mutationFn: (data: RegistrationSchema & { recaptchaToken?: string }) =>
            fetch('/api/eshop/register', {
                method: 'POST',
                body: JSON.stringify(data),
            })
                .then((res) => res.json())
                .then((res) => registrationResponseSchema.safeParse(res)),
        onMutate: () => {
            setError(null)
        },
        onSuccess: (body) => {
            if (!body.success) {
                setError('Unknown error')
            } else if (body.success && body.data.status === 'failed') {
                setError(body.data.error || null)
            } else if (body.success && body.data.status === 'success') {
                setError(null)
                body.data.data.message &&
                    toast.success(t(body.data.data.message))
                if (formData.current) {
                    onDone?.(formData.current.email, formData.current.password)
                    reset()
                    formData.current = null
                }
            }
        },
        onError: (e) => {
            console.error(e)
            setError('Submission failed')
        },
    })

    const {
        mutate: getAresDataM,
        isPending: isLoadingAresData,
        error: aresError,
    } = useMutation({
        mutationFn: (ico: string) =>
            fetch('/api/eshop/firm', {
                method: 'POST',
                body: JSON.stringify({ ico }),
            })
                .then((res) => {
                    if (res.status >= 500) {
                        isAresUnavailable.current = true
                    }
                    return res
                })
                .then((res) => res.json())
                .then((res) => firmResponseSchema.safeParse(res)),
        onMutate: () => {
            isAresUnavailable.current = false
        },
        onSuccess: (body, ico) => {
            if (!body.success) {
                toast.error(t('eshop.registration.aresError'))
                savedIco.current = ''
                return
            }
            savedIco.current = ico
            Object.entries(body.data).forEach(([k, v]) => {
                setValue(k as any, v)
            })
        },
        onError: (e) => {
            savedIco.current = ''
            setIsFirmSectionDisabled(false)
        },
    })

    const onSubmit = useCallback(
        async (data: RegistrationSchema) => {
            formData.current = data
            if (!shouldApplyRecaptcha) {
                mutate(data)
                return
            }
            if (!window.grecaptcha) {
                setError('Security service not loaded.')
                return
            }

            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(
                        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
                        { action: 'registration' }
                    )
                    mutate({ ...data, recaptchaToken: token })
                } catch (e) {
                    console.error('reCAPTCHA Error:', e)
                    setError('Security verification failed to initialize.')
                }
            })
        },
        [mutate, shouldApplyRecaptcha]
    )

    return (
        <>
            {shouldApplyRecaptcha && (
                <Script
                    src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                    strategy="afterInteractive"
                />
            )}

            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-2 pb-10 max-w-2xl mx-auto"
            >
                <EshopFormHeading size="large" className="mb-6">
                    {t('eshop.registration.title')}
                </EshopFormHeading>
                <Heading
                    text={t('eshop.registration.contactPersonTitle')}
                    tag={'h5'}
                    className={clsx('font-bold text-left text-secondary')}
                />
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.name') + ' *'}
                    {...register('name')}
                    error={errors?.name?.message}
                    className={eshopInputClasses}
                    required
                />
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.surname') + ' *'}
                    {...register('surname')}
                    error={errors?.surname?.message}
                    className={eshopInputClasses}
                    required
                />
                <TextInput
                    type="email"
                    placeholder={t('eshop.registration.email') + ' *'}
                    {...register('email')}
                    error={errors?.email?.message}
                    className={eshopInputClasses}
                    required
                />
                <TextInput
                    type="password"
                    placeholder={t('eshop.registration.password') + ' *'}
                    {...register('password')}
                    error={errors?.password?.message}
                    className={eshopInputClasses}
                    required
                />
                <TextInput
                    type="tel"
                    placeholder={t('eshop.registration.phone') + ' *'}
                    {...register('phoneNumber')}
                    error={errors?.phoneNumber?.message}
                    className={eshopInputClasses}
                    required
                />

                <Heading
                    text={t('eshop.registration.companyInfoTitle')}
                    tag={'h5'}
                    className={clsx('font-bold text-left text-secondary mt-4')}
                />

                {country === 'hu' && (
                    <TextInput
                        type="text"
                        placeholder={t('eshop.registration.vat') + ' *'}
                        {...register('VAT')}
                        error={errors?.VAT?.message}
                        className={eshopInputClasses}
                        required
                    />
                )}

                {country === 'cs' && (
                    <>
                        <div className="flex gap-4 items-center">
                            <TextInput
                                type="text"
                                placeholder={t('eshop.registration.vat')}
                                {...register('VAT')}
                                error={errors?.VAT?.message}
                                className={eshopInputClasses}
                            />
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={isLoadingAresData}
                                onClick={() => {
                                    const ico = getValues().VAT
                                    if (ico) getAresDataM(ico)
                                }}
                            >
                                {isLoadingAresData ? (
                                    <Loading noMargin />
                                ) : (
                                    <ArrowDownTrayIcon
                                        className="mx-2"
                                        width={24}
                                        height={24}
                                        title={t(
                                            'eshop.registration.downloadFromAres'
                                        )}
                                    />
                                )}
                            </button>
                        </div>
                        {aresError && !isAresUnavailable.current && (
                            <div className="text-amber-600 text-center">
                                * {t('eshop.registration.aresError')}
                            </div>
                        )}
                        {aresError && isAresUnavailable.current && (
                            <div className="text-red-800 text-center">
                                * {t('eshop.registration.aresUnavailable')}
                            </div>
                        )}
                    </>
                )}
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.firmName') + ' *'}
                    {...register('firmName')}
                    error={errors?.firmName?.message}
                    className={eshopInputClasses}
                    disabled={isFirmSectionDisabled}
                    required
                />
                <Heading
                    text={t('eshop.basket.invoiceAddressHeading')}
                    tag={'h5'}
                    className={clsx('font-bold text-left text-secondary mt-4')}
                />
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.street') + ' *'}
                    {...register('street')}
                    error={errors?.street?.message}
                    className={eshopInputClasses}
                    disabled={isFirmSectionDisabled}
                    required
                />
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.zip') + ' *'}
                    {...register('zip')}
                    error={errors?.zip?.message}
                    className={eshopInputClasses}
                    disabled={isFirmSectionDisabled}
                    required
                />
                <TextInput
                    type="text"
                    placeholder={t('eshop.registration.city') + ' *'}
                    {...register('city')}
                    error={errors?.city?.message}
                    className={eshopInputClasses}
                    disabled={isFirmSectionDisabled}
                    required
                />
                <Controller
                    name="country"
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                        <Select
                            {...rest}
                            placeholder={t('eshop.registration.country') + ' *'}
                            required
                            options={countryOptions}
                            onChange={(newValue) => {
                                onChange((newValue as any).value)
                            }}
                            error={errors?.country?.message}
                        />
                    )}
                />
                <div className="flex justify-center gap-10">
                    <Button url="/eshop">
                        {t('eshop.register.cancelBtn')}
                    </Button>
                    <EshopFormButton disabled={isPending}>
                        {isPending ? (
                            <div className="flex justify-center px-2">
                                <span className="loading loading-spinner"></span>
                            </div>
                        ) : (
                            t('eshop.register.registerBtn')
                        )}
                    </EshopFormButton>
                </div>
                <div className="font-bold mt-4 h-6 text-red-700 text-center">
                    {error ? `${t('eshop.registration.error')}: ${error}` : ''}
                </div>
            </form>
        </>
    )
}
